import $ from "jquery";

import * as blueslip from "./blueslip.ts";

let visible = false;
let current_path: string | undefined;

export function is_visible(): boolean {
    return visible;
}

export function get_current_path(): string | undefined {
    return current_path;
}

function resize_iframe_to_content(): void {
    const $iframe = $("#realm-embed-container iframe");
    if ($iframe.length === 0) {
        return;
    }

    try {
        const iframe = $iframe[0] as HTMLIFrameElement;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        
        if (iframeDoc) {
            // Get the height of the content
            const contentHeight = Math.max(
                iframeDoc.body?.scrollHeight || 0,
                iframeDoc.documentElement?.scrollHeight || 0,
            );
            
            if (contentHeight > 0) {
                // Add a bit of padding to prevent scrollbars
                $iframe.height(contentHeight + 20);
            }
        }
    } catch (error) {
        // Cross-origin iframes will throw errors - that's expected
        // Just keep the default height in those cases
    }
}

function display_in_iframe(url: string): void {
    // Hide message feed and show realm embed container
    $("#message_feed_container").hide();
    const $container = $("#realm-embed-container");
    const $view = $("#realm-embed-view");
    $view.removeClass("hidden").show();

    // Create or update the iframe
    let $iframe = $container.find("iframe");
    if ($iframe.length === 0) {
        $iframe = $("<iframe>")
            .attr({
                sandbox: "allow-same-origin allow-scripts allow-forms allow-popups",
                loading: "eager",
            })
            .css({
                width: "100%",
                minHeight: "500px",
                border: "none",
                display: "block",
            });
        $container.empty().append($iframe);

        // Try to resize when iframe loads
        $iframe.on("load", () => {
            resize_iframe_to_content();
        });
    }

    // Set the iframe source
    $iframe.attr("src", url);

    visible = true;
    current_path = url;
}

function fetch_and_display(path: string): void {
    // Ensure path starts with /
    if (!path.startsWith("/")) {
        path = "/" + path;
    }

    // Hide message feed and show realm embed container
    $("#message_feed_container").hide();
    const $container = $("#realm-embed-container");
    $container.empty().html('<div class="realm-embed-loading">Loading...</div>');
    const $view = $("#realm-embed-view");
    $view.removeClass("hidden").show();

    // Fetch the content directly from the path (served by realm static middleware)
    fetch(path, {
        credentials: "same-origin",
        headers: {
            "X-Requested-With": "XMLHttpRequest",
        },
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.text();
        })
        .then((html) => {
            // Use DOMParser for safer HTML manipulation
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            
            // Remove the existing auto-reload script - we'll inject our own
            const scripts = doc.querySelectorAll("script");
            for (const script of scripts) {
                const script_text = script.textContent || "";
                // Detect and REMOVE auto-reload script 
                if (script_text.includes("realm-static-etag") || 
                    script_text.includes("checkForUpdates")) {
                    script.remove();
                }
            }
            
            // Extract just the body content to avoid nested HTML structure issues
            const body_content = doc.body ? doc.body.innerHTML : html;
            
            // Add explicit padding using inline style
            $container.html('<div style="padding-top: 2.5em;">' + body_content + '</div>');
            
            // Inject our own simpler auto-reload script for embed mode
            inject_embed_reload_script(path);
            
            visible = true;
            current_path = path;
        })
        .catch((error) => {
            blueslip.error("Failed to load realm embed", {path, error: String(error)});
            $container.html(
                `<div class="realm-embed-error">Failed to load content: ${String(error)}</div>`,
            );
            visible = true;
            current_path = path;
        });
}

function inject_embed_reload_script(path: string): void {
    // Create a simpler, dedicated auto-reload script for embed mode
    // This polls for changes and updates the embed container directly
    const script = document.createElement("script");
    script.textContent = `
        (function() {
            let currentETag = null;
            const pagePath = ${JSON.stringify(path)};
            
            function checkForUpdates() {
                fetch(pagePath, {
                    method: 'HEAD',
                    credentials: 'same-origin',
                    headers: { 'X-Requested-With': 'XMLHttpRequest' }
                })
                .then(function(response) {
                    const newETag = response.headers.get('ETag');
                    if (newETag && currentETag && newETag !== currentETag) {
                        // ETag changed - reload the content
                        reloadEmbedContent();
                    }
                    currentETag = newETag;
                })
                .catch(function() {
                    // Silently ignore errors
                });
            }
            
            function reloadEmbedContent() {
                const container = document.getElementById('realm-embed-container');
                if (!container) return;
                
                // Save form state before reloading
                const formState = saveFormState();
                
                fetch(pagePath, {
                    credentials: 'same-origin',
                    headers: { 'X-Requested-With': 'XMLHttpRequest' }
                })
                .then(function(response) { return response.text(); })
                .then(function(html) {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    
                    // Remove any auto-reload scripts from fetched content
                    const scripts = doc.querySelectorAll('script');
                    for (let i = 0; i < scripts.length; i++) {
                        const scriptText = scripts[i].textContent || '';
                        if (scriptText.includes('realm-static-etag') || scriptText.includes('checkForUpdates')) {
                            scripts[i].remove();
                        }
                    }
                    
                    const bodyContent = doc.body ? doc.body.innerHTML : html;
                    container.innerHTML = '<div style="padding-top: 2.5em;">' + bodyContent + '</div>';
                    
                    // Restore form state
                    restoreFormState(formState);
                    
                    // Re-inject this reload script
                    const newScript = document.createElement('script');
                    newScript.textContent = ${JSON.stringify(script.textContent)};
                    container.appendChild(newScript);
                })
                .catch(function() {
                    // Silently ignore errors
                });
            }
            
            function saveFormState() {
                const formState = { inputs: [], textareas: [], selects: [] };
                const container = document.getElementById('realm-embed-container');
                if (!container) return formState;
                
                container.querySelectorAll('input').forEach(function(input, index) {
                    formState.inputs.push({
                        index: index,
                        value: input.value,
                        checked: input.checked,
                        type: input.type
                    });
                });
                
                container.querySelectorAll('textarea').forEach(function(textarea, index) {
                    formState.textareas.push({
                        index: index,
                        value: textarea.value
                    });
                });
                
                container.querySelectorAll('select').forEach(function(select, index) {
                    formState.selects.push({
                        index: index,
                        value: select.value
                    });
                });
                
                return formState;
            }
            
            function restoreFormState(formState) {
                const container = document.getElementById('realm-embed-container');
                if (!container || !formState) return;
                
                formState.inputs.forEach(function(state) {
                    const inputs = container.querySelectorAll('input');
                    if (inputs[state.index]) {
                        inputs[state.index].value = state.value;
                        if (state.type === 'checkbox' || state.type === 'radio') {
                            inputs[state.index].checked = state.checked;
                        }
                    }
                });
                
                formState.textareas.forEach(function(state) {
                    const textareas = container.querySelectorAll('textarea');
                    if (textareas[state.index]) {
                        textareas[state.index].value = state.value;
                    }
                });
                
                formState.selects.forEach(function(state) {
                    const selects = container.querySelectorAll('select');
                    if (selects[state.index]) {
                        selects[state.index].value = state.value;
                    }
                });
            }
            
            // Start polling after a short delay
            setTimeout(function() {
                setInterval(checkForUpdates, 3000); // Check every 3 seconds
            }, 100);
        })();
    `;
    
    const $container = $("#realm-embed-container");
    $container.append(script);
}

export function show(path: string): boolean {
    const previousVisible = visible;
    const previousPath = current_path;

    if (visible && current_path === path) {
        // Already showing this path
        return false;
    }

    // Determine if this is an external URL (with FQDN) or internal path
    const is_external_url = path.startsWith("http://") || path.startsWith("https://");

    try {
        if (is_external_url) {
            // External URLs with FQDN: use iframe
            display_in_iframe(path);
        } else {
            // Internal realm files (relative paths): use AJAX to avoid X-Frame-Options issues
            fetch_and_display(path);
        }
    } catch (error) {
        blueslip.error("Failed to show realm embed", {path, error: String(error)});
        const $container = $("#realm-embed-container");
        $container.html(
            `<div class="realm-embed-error">Failed to load content: ${String(error)}</div>`,
        );
        visible = true;
        current_path = path;
    }

    return !previousVisible || previousPath !== path;
}

export function hide(): boolean {
    if (!visible) {
        return false;
    }

    $("#realm-embed-container").empty();
    $("#realm-embed-view").addClass("hidden").hide();
    $("#message_feed_container").show();

    visible = false;
    current_path = undefined;
    return true;
}

export function initialize(): void {
    // Create the view container if it doesn't exist
    // Place it at the same level as #breakpage-view and #message_feed_container
    if ($("#realm-embed-view").length === 0) {
        const $view = $("<div>")
            .attr("id", "realm-embed-view")
            .addClass("hidden")
            .css("display", "none");
        const $container = $("<div>").attr("id", "realm-embed-container");
        $view.append($container);
        // Insert after #breakpage-view, before #message_feed_container
        $("#breakpage-view").after($view);
    }
}
